/* Customer-auth API surface — TanStack Query mutations.
 *
 * Phase 0 contract stabilization (plan §"Canonical contracts"):
 *   POST /customer-auth/request-otp     → { jti, channel, expiresAt }
 *   POST /customer-auth/verify-otp      → identity + Set-Cookie access (H4: no raw tokens in browser JSON)
 *   POST /customer-auth/link-account    → { transactionId, alreadyLinked }
 *
 * Auth hardening (Option A register):
 *   POST /customer-auth/register        → { status: VERIFICATION_SENT, ... }
 *   POST /customer-auth/confirm-email   → token pair
 *   POST /customer-auth/resend-confirmation
 *   POST /api/v1/customer-auth/set-credentials  (platform-only; session + credentialSetupToken)
 *   POST /customer-auth/forgot-password → { status: RESET_SENT }
 *   POST /customer-auth/reset-password
 *   POST /customer-auth/logout-all
 *
 * Every mutation here goes through ApiClient, so Idempotency-Key is added
 * automatically (callers can override via opts.idempotencyKey when wiring
 * post-pay CTAs that may retry on flaky connections).
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { loadRuntimeConfig } from '../../config/runtimeConfig.js';
import { ApiError } from '../../shared/api/ApiClient.js';
import {
  customerEnvelopeApiError,
  customerInvalidJsonApiError,
} from '../../shared/errors/customerApiParseErrors.js';
import { resolvePublicApiFailureMessage } from '../../shared/errors/publicApiFailureMessage.js';
import { stormAwareFetch } from '../../shared/http/stormAwareFetch.js';
import i18n from '../../i18n.js';
import { customerAuthBodyLocale } from '../../shared/http/platformEnvelopeLocale.js';
import { clearCustomerAccountCache } from '../../shared/api/clearCustomerAccountCache.js';
import { useApiClient } from '../../shared/providers/AppDataProvider.js';
import { useRuntimeConfig } from '../../shared/providers/RuntimeConfigProvider.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { customerAuthCsrfHeaders } from '../../shared/session/customerAuthCsrf.js';
import { endCustomerSessionGlobal } from '../../shared/session/customerAuthSync.js';
import { fetchManualSessionRefreshCoordinated } from '../../shared/session/sessionRefresh.js';
import { fetchPlatformMe } from '../../shared/session/fetchPlatformMe.js';
import {
  beginLoginLand,
  endLoginLand,
} from '../../shared/session/loginLandGate.js';
import { clearReturnTarget } from '../../shared/session/returnTarget.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { publishCustomerAuth } from '../../shared/crossTab/customerCrossTab.js';
import type { AuthConsentsPayload } from './authConsents.js';
import { navigateOidcAuthorizationUrl } from '../../shared/session/oidcPopupBridge.js';
import {
  classifySignInIdentifier,
  normalizePhoneToE164,
  type SignInChannel,
} from './validation.js';
import { type CustomerContactPolicyPayload } from 'pi-kiosk-shared';

/** Platform identity auth — sole mint surface (V2 NE-01). */
function platformCustomerAuthUrl(apiBaseUrl: string, path: string): string {
  const base = apiBaseUrl.replace(/\/+$/, '');
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${base}/api/v1/customer-auth${normalized}`;
}

/** UI locale for Accept-Language + single-language envelope messages (G1). */
function platformAuthUiLocale(): string {
  return i18n.resolvedLanguage ?? i18n.language ?? 'cs';
}

/** JSON platform-auth headers — always include Accept-Language. */
function platformAuthHeaders(
  extra: Record<string, string> = {},
): Record<string, string> {
  return {
    Accept: 'application/json',
    'Accept-Language': platformAuthUiLocale(),
    ...extra,
  };
}

interface PlatformSuccessEnvelope<T> {
  success: true;
  data: T;
}

interface PlatformErrorEnvelope {
  success: false;
  error: string;
  code?: string;
}

async function parsePlatformAuthEnvelope<T>(response: Response): Promise<T> {
  let body: PlatformSuccessEnvelope<T> | PlatformErrorEnvelope;
  try {
    body = (await response.json()) as PlatformSuccessEnvelope<T> | PlatformErrorEnvelope;
  } catch {
    // Public copy only — never "Invalid JSON (500)" in the customer UI.
    throw customerInvalidJsonApiError(response.status);
  }
  if (!response.ok || body.success !== true) {
    throw customerEnvelopeApiError(response.status, body);
  }
  return body.data;
}

async function postPlatformCustomerAuthLogout(
  input: {
    apiBaseUrl: string;
    signal?: AbortSignal;
  },
  api: 'logout' | 'logout-all',
): Promise<boolean> {
  const path = api === 'logout-all' ? '/logout-all' : '/logout';
  const headers: Record<string, string> = platformAuthHeaders({
    'Content-Type': 'application/json',
    'Idempotency-Key':
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `platform-logout-${Date.now()}`,
    ...customerAuthCsrfHeaders(),
  });
  try {
    const response = await stormAwareFetch(
      platformCustomerAuthUrl(input.apiBaseUrl, path),
      {
        method: 'POST',
        headers,
        credentials: 'include',
        signal: input.signal,
        body: JSON.stringify({}),
      },
    );
    return (
      response.ok ||
      (response.status >= 400 && response.status < 500 && response.status !== 429)
    );
  } catch {
    return false;
  }
}

export interface PlatformConfirmEmailCredentialResponse {
  customerId: number;
  emailVerified: true;
  credentialSetupToken: string;
}

export interface PlatformConfirmEmailPendingOtherResponse {
  status: 'PENDING_OTHER_CHANNEL';
  pendingChannel: 'email' | 'phone';
  maskedPendingIdentifier: string;
  customerId: number;
  tenantId: number;
}

export type PlatformConfirmEmailResponse =
  | PlatformConfirmEmailCredentialResponse
  | PlatformConfirmEmailPendingOtherResponse;

export interface PlatformSetCredentialsResponse {
  customerId: number;
  username: string | null;
  credentialSetupComplete: true;
}

/**
 * V2 — platform logout only (NE-01). Kept for test coverage of XOR credential send.
 */
export async function postTenantCustomerAuthLogout(input: {
  apiBaseUrl: string;
  tenantCode: string;
  api?: 'logout' | 'logout-all';
  signal?: AbortSignal;
}): Promise<boolean> {
  void input.tenantCode;
  const api = input.api ?? 'logout';
  return postPlatformCustomerAuthLogout(
    {
      apiBaseUrl: input.apiBaseUrl,
      signal: input.signal,
    },
    api,
  );
}

/** V2 — platform logout revoke, credentials jar only. */
export async function revokeCustomerAuthSession(input: {
  apiBaseUrl: string;
  tenantCode: string;
  api?: 'logout' | 'logout-all';
  signal?: AbortSignal;
}): Promise<boolean> {
  void input.tenantCode;
  const api = input.api ?? 'logout';
  return postPlatformCustomerAuthLogout(
    {
      apiBaseUrl: input.apiBaseUrl,
      signal: input.signal,
    },
    api,
  );
}

export type OidcLoginProvider = 'google' | 'apple';

export interface RequestOtpResponse {
  jti: string;
  channel: SignInChannel;
  expiresAt: string;
  /** When false, verify-otp must include TERMS + PRIVACY consents. */
  hasLegalGrant?: boolean;
  /** Platform (tenant-less) request-otp returns home tenant for verify. */
  tenantCode?: string;
}

export interface VerifyOtpResponse {
  customerId: number;
  tenantId?: number;
  /** Omitted on browser path (H4) — hydrate from `rpapp_customer_access` cookie. */
  accessToken?: string;
  /** Omitted on browser path (H4) — refresh stays HttpOnly cookie only. */
  refreshToken?: string;
  /** Platform login-password returns home tenant; omitted on identity-only login. */
  tenantCode?: string;
}

/**
 * Dual-contact registration: one channel verified, the other still pending.
 * No session / accessToken — do not clear pending or enter onboarding.
 */
export interface PendingOtherChannelResponse {
  status: 'PENDING_OTHER_CHANNEL';
  pendingChannel: 'email' | 'phone';
  maskedPendingIdentifier: string;
  customerId: number;
  tenantId: number;
}

/** both_loose / both_required after one channel — no cookies; Continue later. */
export interface CredentialSetupTokenResponse {
  customerId: number;
  credentialSetupToken: string;
  emailVerified?: boolean;
}

export type VerifyOtpResult =
  | VerifyOtpResponse
  | PendingOtherChannelResponse
  | CredentialSetupTokenResponse;

export type ConfirmEmailResult =
  | VerifyOtpResponse
  | PlatformConfirmEmailResponse
  | PendingOtherChannelResponse
  | CredentialSetupTokenResponse;

export function isPendingOtherChannelResponse(
  data: unknown
): data is PendingOtherChannelResponse {
  if (data === null || typeof data !== 'object') {
    return false;
  }
  const row = data as { status?: unknown; pendingChannel?: unknown };
  return (
    row.status === 'PENDING_OTHER_CHANNEL' &&
    (row.pendingChannel === 'email' || row.pendingChannel === 'phone')
  );
}

export function isCredentialSetupTokenResponse(
  data: unknown,
): data is CredentialSetupTokenResponse {
  if (data === null || typeof data !== 'object') {
    return false;
  }
  if (isPendingOtherChannelResponse(data)) {
    return false;
  }
  const row = data as { credentialSetupToken?: unknown; customerId?: unknown };
  return (
    typeof row.customerId === 'number' &&
    typeof row.credentialSetupToken === 'string' &&
    row.credentialSetupToken.length > 0
  );
}

export function isFullAuthSessionResponse(data: unknown): data is VerifyOtpResponse {
  if (
    data === null ||
    typeof data !== 'object' ||
    isPendingOtherChannelResponse(data) ||
    isCredentialSetupTokenResponse(data)
  ) {
    return false;
  }
  const row = data as { customerId?: unknown };
  return typeof row.customerId === 'number';
}

export interface RegisterVerificationSentResponse {
  status: 'VERIFICATION_SENT';
  channel: SignInChannel | 'both';
  channels?: Array<SignInChannel>;
  maskedIdentifier: string;
  expiresAt?: string;
  /** Phone register only — UX local state; not sent to request-otp API. */
  challengeJti?: string;
  /** Phone register when tenant policy disables SMS OTP transport. */
  hint?: 'SMS_DISABLED';
  email?: {
    maskedIdentifier: string;
    expiresAt?: string;
    e2eVerifyToken?: string;
    e2eCustomerId?: number;
  };
  phone?: {
    maskedIdentifier: string;
    expiresAt?: string;
    challengeJti?: string;
    hint?: 'SMS_DISABLED';
    otpSent?: boolean;
  };
  e2eVerifyToken?: string;
  e2eCustomerId?: number;
  otpSent?: boolean;
}

export interface ForgotPasswordResponse {
  status: 'RESET_SENT';
  maskedIdentifier: string;
}

export interface RequestStepUpResponse {
  jti: string;
  channel: 'STEP_UP';
  expiresAt: string;
}

export interface VerifyStepUpResponse {
  verified: true;
}

export interface LinkAccountResponse {
  transactionId: string;
  alreadyLinked: boolean;
}

interface RequestOtpVars {
  identifier: string;
  idempotencyKey?: string;
  turnstileToken?: string;
}

export function useRequestOtp(): UseMutationResult<
  RequestOtpResponse,
  Error,
  RequestOtpVars
> {
  const { apiBaseUrl } = useRuntimeConfig();
  return useMutation({
    mutationFn: async ({ identifier, turnstileToken }) => {
      const classified = classifySignInIdentifier(identifier);
      if (classified === null) {
        throw new Error(i18n.t('validation.identifier.invalid', { ns: 'customer' }));
      }
      const body =
        classified.channel === 'email'
          ? { email: classified.identifier }
          : { phone: classified.identifier };
      const response = await stormAwareFetch(
        platformCustomerAuthUrl(apiBaseUrl, '/request-otp'),
        {
          method: 'POST',
          headers: platformAuthHeaders({
            'Content-Type': 'application/json',
            'Idempotency-Key': crypto.randomUUID(),
          }),
          credentials: 'include',
          body: JSON.stringify({
            ...body,
            locale: customerAuthBodyLocale(),
            ...(turnstileToken !== undefined && turnstileToken.length > 0
              ? { turnstileToken }
              : {}),
          }),
        },
      );
      return parsePlatformAuthEnvelope<RequestOtpResponse>(response);
    },
  });
}

interface VerifyOtpVars {
  jti: string;
  code: string;
  idempotencyKey?: string;
  /** When true, refresh cookie is persistent (30d). When false, browser session cookie. */
  persistRefreshSession?: boolean;
  consents?: AuthConsentsPayload;
  turnstileToken?: string;
  /** Platform OTP: home tenant from request-otp (tenant-scoped challenge verify). */
  authTenantCode?: string;
  /**
   * @deprecated Wave F — favourites transfer runs on every establish; flag ignored.
   */
  transferGuestFavorites?: boolean;
}

export type AuthenticatedSessionDeps = {
  queryClient: ReturnType<typeof useQueryClient>;
  tenantCode: string;
  establishSession: ReturnType<typeof useCustomerSession>['establishSession'];
  apiBaseUrl: string;
};

/**
 * B2/G3 — cookie land after mint. Resolves `true` only when `/me` 200 matches
 * `customerId` and `establishSession` ran. Fail → `false` (callers/CSP ready+null);
 * never sets durable guest silence here. Land gate is RAM-only via begin/endLoginLand.
 */
async function applyAuthenticatedSession(
  data: Pick<VerifyOtpResponse, 'customerId'>,
  deps: AuthenticatedSessionDeps,
): Promise<boolean> {
  clearCustomerAccountCache(deps.queryClient, deps.tenantCode);
  // G6 — beginLoginLand so concurrent refresh/non-land /me refuse until end
  // (land probe uses forLoginLand; see loginLandGate.ts). Coordinates with G2 await.
  beginLoginLand();
  let landed = false;
  try {
    const me = await fetchPlatformMe(
      { apiBaseUrl: deps.apiBaseUrl },
      undefined,
      { forLoginLand: true },
    );
    if (me.kind !== 'ok') {
      return false;
    }
    if (me.me.customerId !== data.customerId) {
      return false;
    }
    deps.establishSession({ customerId: me.me.customerId });
    landed = true;
    return landed;
  } finally {
    endLoginLand();
  }
}

/**
 * Await land inside mutationFn so mutateAsync / onSuccess only succeed after establish.
 * Wave 4.6 G10 — also used by OIDC phone-gate complete (`{ customerId }` after Set-Cookie).
 * BAN oidcLand=1 as the phone-gate hydrator.
 */
export async function landAuthenticatedSessionOrThrow(
  data: Pick<VerifyOtpResponse, 'customerId'>,
  deps: AuthenticatedSessionDeps,
): Promise<void> {
  const landed = await applyAuthenticatedSession(data, deps);
  if (!landed) {
    throw new ApiError(
      i18n.t('shared.unexpectedError', { ns: 'customer' }),
      401,
      'SESSION_LAND_FAILED',
    );
  }
}

export function useVerifyOtp(): UseMutationResult<
  VerifyOtpResult,
  Error,
  VerifyOtpVars
> {
  const queryClient = useQueryClient();
  const tenantCode = useTenantCode();
  const { apiBaseUrl } = useRuntimeConfig();
  const { establishSession } = useCustomerSession();
  const sessionDeps: AuthenticatedSessionDeps = {
    queryClient,
    tenantCode,
    establishSession,
    apiBaseUrl,
  };
  return useMutation({
    mutationFn: async ({
      jti,
      code,
      idempotencyKey,
      persistRefreshSession,
      consents,
      turnstileToken,
    }): Promise<VerifyOtpResult> => {
      const response = await stormAwareFetch(
        platformCustomerAuthUrl(apiBaseUrl, '/verify-otp'),
        {
          method: 'POST',
          headers: platformAuthHeaders({
            'Content-Type': 'application/json',
            'Idempotency-Key': idempotencyKey ?? crypto.randomUUID(),
          }),
          credentials: 'include',
          body: JSON.stringify({
            jti,
            code,
            ...(persistRefreshSession !== undefined
              ? { persistRefreshSession }
              : {}),
            ...(consents !== undefined ? { consents } : {}),
            ...(turnstileToken !== undefined && turnstileToken.length > 0
              ? { turnstileToken }
              : {}),
          }),
        },
      );
      const data = await parsePlatformAuthEnvelope<VerifyOtpResult>(response);
      if (
        isPendingOtherChannelResponse(data) ||
        isCredentialSetupTokenResponse(data) ||
        !isFullAuthSessionResponse(data)
      ) {
        return data;
      }
      await landAuthenticatedSessionOrThrow(data, sessionDeps);
      return data;
    },
  });
}

interface RequestPhoneVerificationVars {
  phone: string;
  turnstileToken?: string;
}

/**
 * Guest PHONE_VERIFICATION mint/resend (Wave 2/4).
 * Never use for login OTP — that stays `useRequestOtp` / `/request-otp`.
 */
export function useRequestPhoneVerification(): UseMutationResult<
  RequestOtpResponse,
  Error,
  RequestPhoneVerificationVars
> {
  const { apiBaseUrl } = useRuntimeConfig();
  return useMutation({
    mutationFn: async ({ phone, turnstileToken }) => {
      const normalized = normalizePhoneToE164(phone);
      if (normalized === null) {
        throw new Error(i18n.t('validation.phone.invalid', { ns: 'customer' }));
      }
      const response = await stormAwareFetch(
        platformCustomerAuthUrl(apiBaseUrl, '/request-phone-verification'),
        {
          method: 'POST',
          headers: platformAuthHeaders({
            'Content-Type': 'application/json',
            'Idempotency-Key': crypto.randomUUID(),
          }),
          credentials: 'include',
          body: JSON.stringify({
            phone: normalized,
            locale: customerAuthBodyLocale(),
            ...(turnstileToken !== undefined && turnstileToken.length > 0
              ? { turnstileToken }
              : {}),
          }),
        },
      );
      const data = await parsePlatformAuthEnvelope<{
        jti?: string;
        challengeJti?: string;
        expiresAt: string;
        hasLegalGrant?: boolean;
      }>(response);
      const jtiFromBody =
        typeof data.jti === 'string' && data.jti.length > 0 ? data.jti : null;
      const jtiFromChallenge =
        typeof data.challengeJti === 'string' && data.challengeJti.length > 0
          ? data.challengeJti
          : null;
      const jti = jtiFromBody ?? jtiFromChallenge ?? '';
      if (jti.length === 0) {
        throw new Error(i18n.t('shared.unexpectedError', { ns: 'customer' }));
      }
      return {
        jti,
        channel: 'phone',
        expiresAt: data.expiresAt,
        hasLegalGrant: data.hasLegalGrant ?? true,
      } satisfies RequestOtpResponse;
    },
  });
}

interface ConfirmEmailVars {
  token: string;
  persistRefreshSession?: boolean;
  turnstileToken?: string;
}

export function useConfirmEmail(): UseMutationResult<
  ConfirmEmailResult,
  Error,
  ConfirmEmailVars
> {
  const queryClient = useQueryClient();
  const tenantCode = useTenantCode();
  const { apiBaseUrl } = useRuntimeConfig();
  const { establishSession } = useCustomerSession();
  const sessionDeps: AuthenticatedSessionDeps = {
    queryClient,
    tenantCode,
    establishSession,
    apiBaseUrl,
  };
  return useMutation({
    mutationFn: async ({ token, turnstileToken }): Promise<ConfirmEmailResult> => {
      const response = await stormAwareFetch(
        platformCustomerAuthUrl(apiBaseUrl, '/confirm-email'),
        {
          method: 'POST',
          headers: platformAuthHeaders({
            'Content-Type': 'application/json',
            'Idempotency-Key': crypto.randomUUID(),
          }),
          credentials: 'include',
          body: JSON.stringify({
            token,
            ...(turnstileToken !== undefined && turnstileToken.length > 0
              ? { turnstileToken }
              : {}),
          }),
        },
      );
      const data = await parsePlatformAuthEnvelope<ConfirmEmailResult>(response);
      if (isPendingOtherChannelResponse(data)) {
        return data;
      }
      if (isCredentialSetupTokenResponse(data)) {
        return data;
      }
      if (!isFullAuthSessionResponse(data)) {
        return data;
      }
      await landAuthenticatedSessionOrThrow(data, sessionDeps);
      return data;
    },
  });
}

interface ResendConfirmationVars {
  email: string;
  turnstileToken?: string;
}

export function useResendConfirmation(): UseMutationResult<
  RegisterVerificationSentResponse,
  Error,
  ResendConfirmationVars
> {
  const { apiBaseUrl } = useRuntimeConfig();
  return useMutation({
    mutationFn: async ({ email, turnstileToken }) => {
      const response = await stormAwareFetch(
        platformCustomerAuthUrl(apiBaseUrl, '/resend-confirmation'),
        {
          method: 'POST',
          headers: platformAuthHeaders({
            'Content-Type': 'application/json',
            'Idempotency-Key': crypto.randomUUID(),
          }),
          credentials: 'include',
          body: JSON.stringify({
            email,
            locale: customerAuthBodyLocale(),
            ...(turnstileToken !== undefined && turnstileToken.length > 0
              ? { turnstileToken }
              : {}),
          }),
        },
      );
      return parsePlatformAuthEnvelope<RegisterVerificationSentResponse>(response);
    },
  });
}

export function useRefreshSession(): UseMutationResult<
  { customerId: number },
  Error,
  void
> {
  const runtime = useRuntimeConfig();
  const tenantCode = useTenantCode();
  const { applyHydratedSession } = useCustomerSession();
  return useMutation({
    mutationFn: async () => {
      const result = await fetchManualSessionRefreshCoordinated({ config: runtime });
      if (!result.ok) {
        throw new ApiError(
          resolvePublicApiFailureMessage({
            status: result.status,
            code: result.code,
          }),
          result.status,
          result.code,
        );
      }
      const me = await fetchPlatformMe(runtime);
      if (me.kind === 'missing_cred' || me.kind === 'unauthorized') {
        throw new ApiError(
          resolvePublicApiFailureMessage({ status: 401, code: 'UNAUTHORIZED' }),
          401,
          'UNAUTHORIZED',
        );
      }
      if (me.kind === 'ceiling') {
        throw new ApiError(
          resolvePublicApiFailureMessage({ status: 429, code: 'ME_ATTEMPT_CEILING' }),
          429,
          'ME_ATTEMPT_CEILING',
        );
      }
      if (me.kind !== 'ok') {
        throw new ApiError(
          resolvePublicApiFailureMessage({ status: 503, code: 'ME_TRANSIENT' }),
          503,
          'ME_TRANSIENT',
        );
      }
      return { customerId: me.me.customerId };
    },
    onSuccess: (data) => {
      applyHydratedSession({ customerId: data.customerId }, 'cookie-refresh');
      publishCustomerAuth({
        type: 'session-refreshed',
        tenantCode,
        scope: 'platform',
      });
    },
  });
}

export function useLogout(): UseMutationResult<{ loggedOut: boolean }, Error, void> {
  return useMutation({
    mutationFn: async () => {
      // Admin parity: single clear-local-first end (no pre-revoke wait that leaves chrome signed-in).
      await endCustomerSessionGlobal({
        broadcast: true,
        callApi: true,
        api: 'logout',
        reason: 'logout',
      });
      return { loggedOut: true };
    },
  });
}

export function useLogoutAll(): UseMutationResult<{ loggedOut: boolean }, Error, void> {
  const tenantCode = useTenantCode();
  return useMutation({
    mutationFn: async () => {
      clearReturnTarget({ tenantCode });
      await endCustomerSessionGlobal({
        broadcast: true,
        callApi: true,
        api: 'logout-all',
        reason: 'logout',
      });
      return { loggedOut: true };
    },
  });
}

export interface ResetPasswordResponse {
  status: 'PASSWORD_RESET';
}

interface LoginWithPasswordVars {
  identifier: string;
  password: string;
  idempotencyKey?: string;
  persistRefreshSession?: boolean;
  consents?: AuthConsentsPayload;
  turnstileToken?: string;
}

interface RegisterCustomerVars {
  email?: string;
  phone?: string;
  name?: string;
  consents: AuthConsentsPayload;
  persistRefreshSession?: boolean;
  turnstileToken?: string;
  /**
   * Platform `/sign-up` with an org selected — register against that org while
   * chrome stays `__platform__`. Omit for platform-only register (no membership).
   */
  authTenantCode?: string;
}

export function useRegisterCustomer(): UseMutationResult<
  RegisterVerificationSentResponse,
  Error,
  RegisterCustomerVars
> {
  const { apiBaseUrl } = useRuntimeConfig();
  return useMutation({
    mutationFn: async ({
      email,
      phone,
      name,
      consents,
      persistRefreshSession,
      turnstileToken,
      authTenantCode,
    }) => {
      const emailTrim = email?.trim() ?? '';
      const phoneTrim = phone?.trim() ?? '';
      if (emailTrim.length === 0 && phoneTrim.length === 0) {
        throw new Error(i18n.t('validation.contact.atLeastOne', { ns: 'customer' }));
      }
      const response = await stormAwareFetch(
        platformCustomerAuthUrl(apiBaseUrl, '/register'),
        {
          method: 'POST',
          headers: platformAuthHeaders({
            'Content-Type': 'application/json',
            'Idempotency-Key': crypto.randomUUID(),
          }),
          credentials: 'include',
          body: JSON.stringify({
            consents,
            locale: customerAuthBodyLocale(),
            ...(emailTrim.length > 0 ? { email: emailTrim.toLowerCase() } : {}),
            ...(phoneTrim.length > 0 ? { phone: phoneTrim } : {}),
            ...(name !== undefined && name.length > 0 ? { name } : {}),
            ...(authTenantCode !== undefined && authTenantCode.trim().length > 0
              ? { tenantCode: authTenantCode.trim() }
              : {}),
            ...(persistRefreshSession !== undefined
              ? { persistRefreshSession }
              : {}),
            ...(turnstileToken !== undefined && turnstileToken.length > 0
              ? { turnstileToken }
              : {}),
          }),
        },
      );
      return parsePlatformAuthEnvelope<RegisterVerificationSentResponse>(response);
    },
  });
}

export function useLoginWithPassword(): UseMutationResult<
  VerifyOtpResponse,
  Error,
  LoginWithPasswordVars
> {
  const queryClient = useQueryClient();
  const tenantCode = useTenantCode();
  const { apiBaseUrl } = useRuntimeConfig();
  const { establishSession } = useCustomerSession();
  const sessionDeps: AuthenticatedSessionDeps = {
    queryClient,
    tenantCode,
    establishSession,
    apiBaseUrl,
  };
  return useMutation({
    mutationFn: async ({
      identifier,
      password,
      idempotencyKey,
      persistRefreshSession,
      consents,
      turnstileToken,
    }) => {
      const response = await stormAwareFetch(
        platformCustomerAuthUrl(apiBaseUrl, '/login-password'),
        {
          method: 'POST',
          headers: platformAuthHeaders({
            'Content-Type': 'application/json',
            'Idempotency-Key': idempotencyKey ?? crypto.randomUUID(),
          }),
          credentials: 'include',
          body: JSON.stringify({
            identifier,
            password,
            ...(persistRefreshSession !== undefined
              ? { persistRefreshSession }
              : {}),
            ...(consents !== undefined ? { consents } : {}),
            ...(turnstileToken !== undefined && turnstileToken.length > 0
              ? { turnstileToken }
              : {}),
          }),
        },
      );
      const data = await parsePlatformAuthEnvelope<VerifyOtpResponse>(response);
      await landAuthenticatedSessionOrThrow(data, sessionDeps);
      return data;
    },
  });
}

interface SetCredentialsVars {
  username?: string;
  password: string;
  /** Platform chrome only — one-time token from confirm-email `credentialSetupToken`. */
  token?: string;
}

export function useSetCredentials(): UseMutationResult<
  { credentialsUpdated: boolean } | PlatformSetCredentialsResponse,
  Error,
  SetCredentialsVars
> {
  const { apiBaseUrl } = useRuntimeConfig();
  return useMutation({
    mutationFn: async ({
      username,
      password,
      token,
    }): Promise<
      { credentialsUpdated: boolean } | PlatformSetCredentialsResponse
    > => {
      if (token !== undefined && token.length > 0) {
        const response = await stormAwareFetch(
          platformCustomerAuthUrl(apiBaseUrl, '/set-credentials'),
          {
            method: 'POST',
            headers: platformAuthHeaders({
              'Content-Type': 'application/json',
              'Idempotency-Key': crypto.randomUUID(),
              ...customerAuthCsrfHeaders(),
            }),
            credentials: 'include',
            body: JSON.stringify({
              token,
              password,
              locale: customerAuthBodyLocale(),
              ...(username !== undefined && username.length > 0
                ? { username }
                : {}),
            }),
          },
        );
        return parsePlatformAuthEnvelope<PlatformSetCredentialsResponse>(response);
      }
      const response = await stormAwareFetch(
        platformCustomerAuthUrl(apiBaseUrl, '/set-credentials'),
        {
          method: 'POST',
          headers: platformAuthHeaders({
            'Content-Type': 'application/json',
            'Idempotency-Key': crypto.randomUUID(),
            ...customerAuthCsrfHeaders(),
          }),
          credentials: 'include',
          body: JSON.stringify({
            locale: customerAuthBodyLocale(),
            ...(username !== undefined && username.length > 0 ? { username } : {}),
            password,
          }),
        },
      );
      const data = await parsePlatformAuthEnvelope<{ credentialsUpdated: boolean }>(
        response,
      );
      return data;
    },
  });
}

interface ForgotPasswordVars {
  identifier: string;
  turnstileToken?: string;
}

export function useForgotPassword(): UseMutationResult<
  ForgotPasswordResponse,
  Error,
  ForgotPasswordVars
> {
  const { apiBaseUrl } = useRuntimeConfig();
  return useMutation({
    mutationFn: async ({ identifier, turnstileToken }) => {
      const response = await stormAwareFetch(
        platformCustomerAuthUrl(apiBaseUrl, '/forgot-password'),
        {
          method: 'POST',
          headers: platformAuthHeaders({
            'Content-Type': 'application/json',
            'Idempotency-Key': crypto.randomUUID(),
          }),
          credentials: 'include',
          body: JSON.stringify({
            identifier,
            locale: customerAuthBodyLocale(),
            ...(turnstileToken !== undefined && turnstileToken.length > 0
              ? { turnstileToken }
              : {}),
          }),
        },
      );
      return parsePlatformAuthEnvelope<ForgotPasswordResponse>(response);
    },
  });
}

interface ResetPasswordVars {
  token: string;
  password: string;
  turnstileToken?: string;
}

export function useResetPassword(): UseMutationResult<
  ResetPasswordResponse,
  Error,
  ResetPasswordVars
> {
  const queryClient = useQueryClient();
  const tenantCode = useTenantCode();
  const { apiBaseUrl } = useRuntimeConfig();
  return useMutation({
    mutationFn: async ({ token, password, turnstileToken }) => {
      const response = await stormAwareFetch(
        platformCustomerAuthUrl(apiBaseUrl, '/reset-password'),
        {
          method: 'POST',
          headers: platformAuthHeaders({
            'Content-Type': 'application/json',
            'Idempotency-Key': crypto.randomUUID(),
          }),
          credentials: 'include',
          body: JSON.stringify({
            token,
            password,
            locale: customerAuthBodyLocale(),
            ...(turnstileToken !== undefined && turnstileToken.length > 0
              ? { turnstileToken }
              : {}),
          }),
        },
      );
      return parsePlatformAuthEnvelope<ResetPasswordResponse>(response);
    },
    onSuccess: () => {
      clearCustomerAccountCache(queryClient, tenantCode);
      void endCustomerSessionGlobal({
        broadcast: true,
        callApi: false,
        reason: 'revoked',
      });
    },
  });
}

interface LinkAccountVars {
  paymentId: string;
  customerId: number;
  idempotencyKey?: string;
  /** Org tenant when chrome is platform after OIDC land. */
  tenantCode?: string;
}

const LINK_ACCOUNT_TIMEOUT_MS = 12_000;

export function useLinkAccountToTransaction(): UseMutationResult<
  LinkAccountResponse,
  Error,
  LinkAccountVars
> {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const tenantCode = useTenantCode();
  return useMutation({
    mutationFn: async ({ paymentId, customerId, idempotencyKey, tenantCode: overrideTenant }) => {
      const abortController = new AbortController();
      const timeoutId = window.setTimeout(() => {
        abortController.abort();
      }, LINK_ACCOUNT_TIMEOUT_MS);
      try {
        return await api.request<LinkAccountResponse>('/customer-auth/link-account', {
          method: 'POST',
          body: { paymentId, customerId },
          idempotencyKey,
          signal: abortController.signal,
          ...(overrideTenant !== undefined && overrideTenant.trim().length > 0
            ? { tenantCode: overrideTenant.trim() }
            : {}),
        });
      } catch (error) {
        if (
          error instanceof Error &&
          (error.name === 'AbortError' || error.message.includes('aborted'))
        ) {
          throw new Error(i18n.t('errors.linkAccountTimeout', { ns: 'customer' }));
        }
        throw error;
      } finally {
        window.clearTimeout(timeoutId);
      }
    },
    onSuccess: async (_data, vars) => {
      // Objednávky remounts with ACCOUNT_QUERY_OPTIONS.refetchOnMount: false.
      // removeQueries forces a fresh fetch; invalidate alone can leave empty cache
      // when there are no active observers.
      const paymentsTenant = vars.tenantCode ?? tenantCode;
      queryClient.removeQueries({
        queryKey: ['customer', 'account', vars.customerId, 'payments'],
      });
      queryClient.removeQueries({
        queryKey: ['customer', paymentsTenant, 'account', vars.customerId, 'payments'],
      });
      await queryClient.invalidateQueries({
        queryKey: ['customer-orders'],
      });
    },
  });
}

export function useRequestStepUp(): UseMutationResult<
  RequestStepUpResponse,
  Error,
  { turnstileToken?: string } | void
> {
  const api = useApiClient();
  return useMutation({
    mutationFn: (vars) =>
      api.request<RequestStepUpResponse>('/customer-auth/request-step-up', {
        method: 'POST',
        body: {
          locale: customerAuthBodyLocale(),
          ...(vars?.turnstileToken !== undefined && vars.turnstileToken.length > 0
            ? { turnstileToken: vars.turnstileToken }
            : {}),
        },
      }),
  });
}

interface VerifyStepUpVars {
  jti: string;
  code: string;
  turnstileToken?: string;
}

export function useVerifyStepUp(): UseMutationResult<
  VerifyStepUpResponse,
  Error,
  VerifyStepUpVars
> {
  const api = useApiClient();
  return useMutation({
    mutationFn: ({ jti, code, turnstileToken }) =>
      api.request<VerifyStepUpResponse>('/customer-auth/verify-step-up', {
        method: 'POST',
        body: {
          jti,
          code,
          ...(turnstileToken !== undefined && turnstileToken.length > 0
            ? { turnstileToken }
            : {}),
        },
      }),
  });
}

export interface OidcStatusResponse {
  oidcLoginEnabled: boolean;
  googleEnabled: boolean;
  appleEnabled: boolean;
}

export interface SocialAuthVisibility {
  showGoogle: boolean;
  showApple: boolean;
  showAny: boolean;
}

export function resolveSocialAuthVisibility(
  status: OidcStatusResponse | undefined,
): SocialAuthVisibility {
  const oidcLoginEnabled = status?.oidcLoginEnabled === true;
  const showGoogle = oidcLoginEnabled && status?.googleEnabled === true;
  const showApple = oidcLoginEnabled && status?.appleEnabled === true;
  return {
    showGoogle,
    showApple,
    showAny: showGoogle || showApple,
  };
}

export function useOidcLoginStatus(): UseQueryResult<OidcStatusResponse, Error> {
  const tenantCode = useTenantCode();
  const { apiBaseUrl } = useRuntimeConfig();
  return useQuery({
    queryKey: ['customer-auth', 'oidc', 'status', tenantCode],
    queryFn: async () => {
      const response = await stormAwareFetch(
        platformCustomerAuthUrl(apiBaseUrl, '/oidc/status'),
        {
          method: 'GET',
          headers: platformAuthHeaders(),
          credentials: 'include',
        },
      );
      return parsePlatformAuthEnvelope<OidcStatusResponse>(response);
    },
    staleTime: 60_000,
    // Public capability probe — retry briefly so Vite cold-start / API boot
    // does not permanently hide Google (retry:false left buttons missing).
    retry: 2,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });
}

export function useContactPolicy(): UseQueryResult<
  CustomerContactPolicyPayload,
  Error
> {
  const tenantCode = useTenantCode();
  const { apiBaseUrl } = useRuntimeConfig();
  return useQuery({
    queryKey: ['customer-auth', 'contact-policy', tenantCode],
    queryFn: async () => {
      const response = await stormAwareFetch(
        platformCustomerAuthUrl(apiBaseUrl, '/contact-policy'),
        {
          method: 'GET',
          headers: platformAuthHeaders(),
          credentials: 'include',
        },
      );
      return parsePlatformAuthEnvelope<CustomerContactPolicyPayload>(response);
    },
    staleTime: 60_000,
    retry: false,
    refetchOnReconnect: false,
  });
}

/**
 * Membership-scoped OIDC start (register / visit-tenant): POST JSON only
 * `{ provider, consents?, returnTo?, turnstileToken?, intent }` (+ CSRF header).
 * Defaults `intent: 'register'` — this helper is register/membership only (G4).
 * Navigate via 3xx `Location` or JSON `{ authorizationUrl }`.
 * Intentionally no GET `oidc/start` assign fallback — that path cannot carry consents (G20).
 * Google/Apple **login** is always tenantless → `platformRedirectToOidcLogin` (never this helper).
 * Empty/platform tenant here is invalid — use `platformRedirectToOidcLogin` or
 * `platformRedirectToOidcRegister`, or a real path tenant for membership registration.
 */
export async function redirectToOidcLogin(
  tenantCode: string,
  provider: OidcLoginProvider,
  consents?: AuthConsentsPayload,
  returnTo?: string,
  turnstileToken?: string,
  intent: 'login' | 'register' = 'register',
): Promise<void> {
  const trimmedTenant = tenantCode.trim();
  if (trimmedTenant.length === 0 || trimmedTenant === '__platform__') {
    throw new ApiError(
      resolvePublicApiFailureMessage({ status: 400, code: 'TENANT_REQUIRED' }),
      400,
      'TENANT_REQUIRED',
    );
  }
  const config = loadRuntimeConfig();
  const postUrl = platformCustomerAuthUrl(config.apiBaseUrl, '/oidc/start');

  await assignFromOidcStartResponse(postUrl, {
    provider,
    tenantCode: trimmedTenant,
    intent,
    ...(returnTo !== undefined ? { returnTo } : {}),
    ...(consents !== undefined ? { consents } : {}),
    ...(turnstileToken !== undefined && turnstileToken.length > 0
      ? { turnstileToken }
      : {}),
  });
}

/**
 * Platform chrome `/sign-in` — Customer-only OIDC login (no org picker, no consent ticks).
 * POST `/api/v1/customer-auth/oidc/start` without tenantCode, `intent: 'login'`.
 * Navigates via same-window `window.location.assign` to the IdP authorize URL.
 */
export async function platformRedirectToOidcLogin(
  provider: OidcLoginProvider,
  returnTo: string = '/account',
  turnstileToken?: string,
): Promise<void> {
  const config = loadRuntimeConfig();
  const postUrl = `${config.apiBaseUrl.replace(/\/+$/, '')}/api/v1/customer-auth/oidc/start`;

  await assignFromOidcStartResponse(postUrl, {
    provider,
    returnTo,
    intent: 'login',
    ...(turnstileToken !== undefined && turnstileToken.length > 0
      ? { turnstileToken }
      : {}),
  });
}

/**
 * Platform chrome `/sign-up` with empty org — Customer-only OIDC (no forced membership).
 * POST `/api/v1/customer-auth/oidc/start` without tenantCode.
 */
export async function platformRedirectToOidcRegister(
  provider: OidcLoginProvider,
  consents: AuthConsentsPayload,
  returnTo: string = '/sign-in',
  turnstileToken?: string,
): Promise<void> {
  const config = loadRuntimeConfig();
  const postUrl = `${config.apiBaseUrl.replace(/\/+$/, '')}/api/v1/customer-auth/oidc/start`;

  await assignFromOidcStartResponse(postUrl, {
    provider,
    returnTo,
    consents,
    intent: 'register',
    ...(turnstileToken !== undefined && turnstileToken.length > 0
      ? { turnstileToken }
      : {}),
  });
}

async function assignFromOidcStartResponse(
  postUrl: string,
  body: Record<string, unknown>,
): Promise<void> {
  const response = await stormAwareFetch(postUrl, {
    method: 'POST',
    headers: platformAuthHeaders({
      'Content-Type': 'application/json',
      ...customerAuthCsrfHeaders(),
    }),
    credentials: 'include',
    redirect: 'manual',
    body: JSON.stringify(body),
  });

  const locationHeader = response.headers.get('Location');
  if (
    locationHeader !== null &&
    locationHeader.length > 0 &&
    ((response.status >= 300 && response.status < 400) || response.status === 0)
  ) {
    navigateOidcAuthorizationUrl(new URL(locationHeader, postUrl).href);
    return;
  }

  type OidcStartJsonBody = {
    success?: boolean;
    data?: { authorizationUrl?: string };
    error?: string;
    code?: string;
  };
  let parsedBody: OidcStartJsonBody | null = null;
  try {
    parsedBody = (await response.json()) as OidcStartJsonBody;
  } catch {
    parsedBody = null;
  }

  const authorizationUrl = parsedBody?.data?.authorizationUrl;
  if (
    response.ok &&
    parsedBody?.success === true &&
    typeof authorizationUrl === 'string' &&
    authorizationUrl.length > 0
  ) {
    navigateOidcAuthorizationUrl(authorizationUrl);
    return;
  }

  throw customerEnvelopeApiError(response.status, parsedBody);
}
